import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LABELS, RISK_EVENT_KINDS, type RiskEventKind } from "@match/shared";
import { PageHeader, EmptyState } from "@/components/Layout";
import { Button, ErrorNote } from "@/components/ui";
import { api, errorMessage, type RiskPeriod } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { useOpenRiskEvents, useRiskEvents } from "@/lib/queries";
import { useQueryClient } from "@tanstack/react-query";
import type { RiskEventRow } from "@/lib/types";

/*
 * Eventi di rischio.
 *
 * Due schede, e sono due domande diverse: «che cosa è rotto ADESSO» e «è già
 * successo?». La prima è corta per costruzione — gli informativi non ci entrano
 * e chi rientra da sé si chiude da sé — e se diventa lunga il problema non è la
 * lista.
 *
 * Un evento non si cancella e non si modifica: è una prova. L'unica azione è
 * «Chiudi» (una riga o tutte quelle del filtro), che lo toglie dagli aperti e
 * lascia scritto CHI l'ha chiuso. Se la condizione è ancora vera, la guardia
 * lo rialza al giro dopo: chiudere non nasconde.
 */

const TABS = [
  { key: "aperti", label: "Aperti" },
  { key: "storico", label: "Storico" },
] as const;

const PAGE = 50;

export function Events() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.find((t) => t.key === params.get("vista")) ?? TABS[0];

  return (
    <>
      <PageHeader title="Eventi" subtitle="Che cosa è rotto adesso, e che cosa è successo" />

      <div className="border-b border-line px-6">
        <nav className="flex gap-1" aria-label="Eventi">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => {
                const next = new URLSearchParams(params);
                if (t.key === "aperti") next.delete("vista");
                else next.set("vista", t.key);
                setParams(next, { replace: true });
              }}
              aria-current={t.key === tab.key ? "page" : undefined}
              className={[
                "-mb-px border-b-2 px-3 py-2.5 text-sm transition-colors",
                t.key === tab.key ? "border-ink font-medium text-ink" : "border-transparent text-muted hover:text-ink",
              ].join(" ")}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab.key === "aperti" ? <Open /> : <History />}
    </>
  );
}

// ---------------------------------------------------------------------------

const PERIODS: { key: RiskPeriod; label: string }[] = [
  { key: "today", label: "Oggi" },
  { key: "7d", label: "Ultimi 7 giorni" },
  { key: "all", label: "Tutto" },
];

const selectClass = "rounded border border-line bg-surface px-2 py-1.5 text-sm";

/** Il periodo a pulsanti: il giorno è quello di Europe/Rome, lo calcola il backend. */
function PeriodPicker({ value, onChange }: { value: RiskPeriod; onChange: (p: RiskPeriod) => void }) {
  return (
    <div role="group" aria-label="Periodo" className="flex flex-wrap gap-1 rounded border border-line bg-raised p-1">
      {PERIODS.map((o) => (
        <button
          key={o.key}
          type="button"
          aria-pressed={value === o.key}
          onClick={() => onChange(o.key)}
          className={["rounded px-2.5 py-1 text-xs font-medium transition-colors", value === o.key ? "bg-surface text-ink shadow-sm" : "text-muted hover:text-ink"].join(" ")}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function KindSelect({ value, onChange }: { value: RiskEventKind | ""; onChange: (k: RiskEventKind | "") => void }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value as RiskEventKind | "")} className={selectClass}>
      <option value="">Tutti i tipi</option>
      {RISK_EVENT_KINDS.map((k) => (
        <option key={k} value={k}>
          {LABELS.riskEventKind[k]}
        </option>
      ))}
    </select>
  );
}

/*
 * «Vai»: solo per gli eventi che chiedono un intervento, e porta dove lo si fa.
 * Gli altri — un conto bruciato, specifiche o commissione cambiate, il gateway
 * o il proxy giù — non hanno un punto dell'applicazione in cui sistemarli.
 */
const TO_OPERATION = new Set<RiskEventKind>([
  "NAKED_LEG_TIMEOUT",
  "EXECUTION_DESYNC",
  "LEVELS_DESYNC",
  "LEG_VANISHED",
  "PAIR_ABANDONED",
  "ORDER_LANDED_LATE",
  "ORDER_REASON_UNEXPECTED",
]);
const TO_ACCOUNT = new Set<RiskEventKind>(["ACCOUNT_DISCONNECTED", "CURRENCY_MISMATCH"]);
const TO_PHASE = new Set<RiskEventKind>(["DD_THRESHOLD", "DAILY_DD_THRESHOLD"]);

/** Dove si sistema l'evento, o null. Senza l'id che serve non c'è «Vai»: niente destinazioni indovinate. */
export function eventTarget(e: RiskEventRow): string | null {
  // La scheda del conto sta dentro quella del cliente.
  const account = e.account ? `/clienti/${e.account.client.id}` : null;
  if (TO_OPERATION.has(e.kind)) {
    // Ordine in ritardo o di origine inattesa su una singola: nessuna coppia, la pagina è quella del conto.
    if ((e.kind === "ORDER_LANDED_LATE" || e.kind === "ORDER_REASON_UNEXPECTED") && !e.pairId && e.accountId) return `/conti/${e.accountId}/operazione`;
    // Un `EXECUTION_DESYNC` senza Fase è il conto non più sorvegliato (`unwatched:<conto>`).
    return e.sessionId ? `/operazione?fase=${e.sessionId}` : account;
  }
  if (TO_ACCOUNT.has(e.kind)) return account;
  if (TO_PHASE.has(e.kind)) return e.sessionId ? `/fasi/${e.sessionId}` : null;
  if (e.kind === "BROKER_CASH_MOVE") return e.account ? `/clienti/${e.account.client.id}/movimenti` : null;
  if (e.kind === "PAIR_CLOSED") return e.sessionId ? `/fasi/${e.sessionId}` : null;
  return null;
}

function Open() {
  const qc = useQueryClient();
  const [period, setPeriod] = useState<RiskPeriod>("today");
  const [kind, setKind] = useState<RiskEventKind | "">("");
  const [severity, setSeverity] = useState<"" | "WARNING" | "CRITICAL" | "NOTICE">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filter = { period, ...(kind ? { kind: [kind] } : {}), ...(severity ? { severity: [severity] } : {}) };
  const q = useOpenRiskEvents(filter);
  const rows = q.data?.rows ?? [];
  const filtered = period !== "today" || !!kind || !!severity;

  const closeAll = async () => {
    if (!window.confirm(`Chiudere ${rows.length} ${rows.length === 1 ? "evento aperto" : "eventi aperti"}? Restano nello storico col tuo nome.`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.risk.resolveBulk({ ids: rows.map((e) => e.id), ...filter });
      await Promise.all([qc.invalidateQueries({ queryKey: ["risk"] }), qc.invalidateQueries({ queryKey: ["sessions"] })]);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 px-6 py-4">
      <div className="flex flex-wrap items-center gap-3">
        <PeriodPicker value={period} onChange={setPeriod} />
        <KindSelect value={kind} onChange={setKind} />
        <select value={severity} onChange={(e) => setSeverity(e.target.value as typeof severity)} className={selectClass}>
          <option value="">Tutte le gravità</option>
          {(["CRITICAL", "WARNING", "NOTICE"] as const).map((s) => (
            <option key={s} value={s}>
              {LABELS.riskSeverity[s]}
            </option>
          ))}
        </select>
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted">Caricamento…</p>
      ) : q.error ? (
        <ErrorNote>{errorMessage(q.error)}</ErrorNote>
      ) : !rows.length && !q.data?.older && !filtered ? (
        <EmptyState
          title="Niente di rotto"
          hint="Gli eventi informativi — un conto bruciato, per esempio — non compaiono qui: non c'è niente da chiudere. Lo storico li tiene tutti."
        />
      ) : (
        <>
          {rows.length ? (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-sm text-muted">
                  {q.data!.counts.critical > 0 && <span className="text-loss">{q.data!.counts.critical} critici</span>}
                  {q.data!.counts.critical > 0 && q.data!.counts.warning > 0 && " · "}
                  {q.data!.counts.warning > 0 && <span className="text-warn">{q.data!.counts.warning} avvisi</span>}
                  {q.data!.counts.notice > 0 && (
                    <>
                      {(q.data!.counts.critical > 0 || q.data!.counts.warning > 0) && " · "}
                      <span className="text-blue-500">{q.data!.counts.notice} {q.data!.counts.notice === 1 ? "notifica" : "notifiche"}</span>
                    </>
                  )}
                </p>
                <div className="ml-auto">
                  <Button size="sm" onClick={closeAll} disabled={busy} title="Chiude gli aperti di questa tabella, col tuo nome accanto. Le righe restano nello storico.">
                    {busy ? "…" : filtered ? "Chiudi tutti quelli di questo filtro" : "Chiudi tutti quelli di oggi"}
                  </Button>
                </div>
              </div>
              {error && <ErrorNote>{error}</ErrorNote>}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line text-left text-xs text-faint">
                      <th className="py-2 pr-4 font-medium">Quando</th>
                      <th className="py-2 pr-4 font-medium">Tipo</th>
                      <th className="py-2 pr-4 font-medium">Dove</th>
                      <th className="py-2 pr-4 font-medium">Che cosa</th>
                      <th className="py-2 text-right font-medium">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((e) => (
                      <OpenRow key={e.id} e={e} />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p className="card px-4 py-4 text-sm text-muted">Nessun evento aperto con questi filtri.</p>
          )}
          {!!q.data?.older && (
            <p className="text-sm text-muted">
              {q.data.older === 1 ? "1 aperto più vecchio" : `${q.data.older} aperti più vecchi`} del periodo.{" "}
              <button type="button" onClick={() => setPeriod("all")} className="text-ink underline-offset-2 hover:underline">
                Tutto
              </button>
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** Una riga degli aperti: le colonne dello storico, più «Vai» e «Chiudi». */
function OpenRow({ e }: { e: RiskEventRow }) {
  const { busy, error, resolve } = useResolve(e.id);
  const to = eventTarget(e);
  return (
    <tr className="border-b border-line/60 align-top">
      <td className="py-2 pr-4 whitespace-nowrap text-muted">{dateTime(e.createdAt)}</td>
      <td className="py-2 pr-4">
        <span className="flex items-baseline gap-2">
          <SeverityDot severity={e.severity} />
          <span className="text-ink">{LABELS.riskEventKind[e.kind]}</span>
        </span>
      </td>
      <td className="py-2 pr-4 text-muted">
        <Where e={e} />
      </td>
      <td className="py-2 pr-4 text-muted">
        {e.message}
        {error && <ErrorNote>{error}</ErrorNote>}
      </td>
      <td className="py-2 whitespace-nowrap text-right">
        {to && (
          <Link to={to} className="mr-1 inline-flex rounded px-2 py-1 text-xs font-medium text-muted hover:text-ink hover:underline">
            Vai
          </Link>
        )}
        <Button size="sm" variant="ghost" onClick={resolve} disabled={busy} title="Esce dagli aperti, con il tuo nome accanto. La riga resta.">
          {busy ? "…" : "Chiudi"}
        </Button>
      </td>
    </tr>
  );
}

/** Conto e cliente dell'evento, come nello storico. */
function Where({ e }: { e: RiskEventRow }) {
  return (
    <>
      {e.account?.label ?? "—"}
      {e.session && (
        <>
          {" · "}
          <Link to={`/fasi/${e.session.id}`} className="hover:text-ink hover:underline">
            {e.session.client.name}
          </Link>
        </>
      )}
    </>
  );
}

/** «Chiudi» su un evento: la stessa scrittura della card e della riga. */
function useResolve(id: string) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resolve = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.risk.resolve(id);
      await Promise.all([qc.invalidateQueries({ queryKey: ["risk"] }), qc.invalidateQueries({ queryKey: ["sessions"] })]);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, resolve };
}

/** Un evento aperto: che cos'è, dove, e il pulsante che lo chiude. */
export function EventCard({ e, compact = false }: { e: RiskEventRow; compact?: boolean }) {
  const { busy, error, resolve } = useResolve(e.id);

  return (
    <div className={`card ${compact ? "p-3" : "p-4"} ${e.severity === "CRITICAL" ? "border-loss/40" : e.severity === "NOTICE" ? "border-blue-500/40" : "border-warn/40"}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <SeverityDot severity={e.severity} />
        <span className="font-medium text-ink">{LABELS.riskEventKind[e.kind]}</span>
        <span className="text-xs text-faint">{dateTime(e.createdAt)}</span>
        {e.account && (
          <span className={e.account.role === "PROP" ? "text-prop" : "text-broker"}>
            {e.account.label} <span className="machine-id text-xs">{e.account.mt5Login}</span>
          </span>
        )}
        {e.session && (
          <Link to={`/fasi/${e.session.id}`} className="text-muted hover:text-ink hover:underline">
            {e.session.client.name} · {e.session.canonicalSymbol}
          </Link>
        )}
        <div className="ml-auto">
          <Button size="sm" variant="ghost" onClick={resolve} disabled={busy} title="Esce dagli aperti, con il tuo nome accanto. La riga resta.">
            {busy ? "…" : "Chiudi"}
          </Button>
        </div>
      </div>
      <p className="mt-1 text-sm text-muted">{e.message}</p>
      {error && <ErrorNote>{error}</ErrorNote>}
    </div>
  );
}

function SeverityDot({ severity }: { severity: RiskEventRow["severity"] }) {
  const tone = severity === "CRITICAL" ? "bg-loss" : severity === "WARNING" ? "bg-warn" : severity === "NOTICE" ? "bg-blue-500" : "bg-muted";
  return <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${tone}`} title={LABELS.riskSeverity[severity]} aria-label={LABELS.riskSeverity[severity]} />;
}

// ---------------------------------------------------------------------------

function History() {
  // «Tutto» di partenza: lo storico serve a capire se una cosa è già successa.
  const [period, setPeriod] = useState<RiskPeriod>("all");
  const [kind, setKind] = useState<RiskEventKind | "">("");
  // Niente INFO: dal 28/10 non nascono, e quelli vecchi il backend non li mostra.
  const [severity, setSeverity] = useState<"" | "WARNING" | "CRITICAL" | "NOTICE">("");
  const [state, setState] = useState<"" | "open" | "resolved">("");
  const [offset, setOffset] = useState(0);

  const q = useRiskEvents({
    period,
    ...(kind ? { kind: [kind] } : {}),
    ...(severity ? { severity: [severity] } : {}),
    ...(state ? { state } : {}),
    limit: PAGE,
    offset,
  });

  const reset = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setOffset(0);
  };

  return (
    <div className="space-y-4 px-6 py-4">
      <div className="flex flex-wrap items-center gap-3">
        <PeriodPicker value={period} onChange={reset(setPeriod)} />
        <KindSelect value={kind} onChange={reset(setKind)} />
        <select value={severity} onChange={(e) => reset(setSeverity)(e.target.value as typeof severity)} className={selectClass}>
          <option value="">Tutte le gravità</option>
          {(["CRITICAL", "WARNING", "NOTICE"] as const).map((s) => (
            <option key={s} value={s}>
              {LABELS.riskSeverity[s]}
            </option>
          ))}
        </select>
        <select value={state} onChange={(e) => reset(setState)(e.target.value as typeof state)} className={selectClass}>
          <option value="">Aperti e chiusi</option>
          <option value="open">Solo aperti</option>
          <option value="resolved">Solo chiusi</option>
        </select>
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted">Caricamento…</p>
      ) : q.error ? (
        <ErrorNote>{errorMessage(q.error)}</ErrorNote>
      ) : !q.data?.rows.length ? (
        <p className="card px-4 py-4 text-sm text-muted">Nessun evento con questi filtri.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs text-faint">
                  <th className="py-2 pr-4 font-medium">Quando</th>
                  <th className="py-2 pr-4 font-medium">Tipo</th>
                  <th className="py-2 pr-4 font-medium">Dove</th>
                  <th className="py-2 pr-4 font-medium">Che cosa</th>
                  <th className="py-2 font-medium">Chiuso</th>
                </tr>
              </thead>
              <tbody>
                {q.data.rows.map((e) => (
                  <tr key={e.id} className="border-b border-line/60 align-top">
                    <td className="py-2.5 pr-4 whitespace-nowrap text-muted">{dateTime(e.createdAt)}</td>
                    <td className="py-2.5 pr-4">
                      <span className="flex items-baseline gap-2">
                        <SeverityDot severity={e.severity} />
                        <span className="text-ink">{LABELS.riskEventKind[e.kind]}</span>
                      </span>
                    </td>
                    <td className="py-2.5 pr-4 text-muted">
                      <Where e={e} />
                    </td>
                    <td className="py-2.5 pr-4 text-muted">{e.message}</td>
                    <td className="py-2.5 text-xs text-faint">
                      {!e.resolvedAt ? (
                        <span className="text-warn">aperto</span>
                      ) : e.resolvedBy ? (
                        /* Preso in carico: c'è un nome. Senza nome è rientrato da sé. */
                        <>
                          {dateTime(e.resolvedAt)}
                          <br />
                          {e.resolvedBy.fullName ?? e.resolvedBy.email}
                        </>
                      ) : (
                        <>
                          {dateTime(e.resolvedAt)}
                          <br />
                          da sé
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pager total={q.data.total} limit={q.data.limit} offset={offset} onOffset={setOffset} />
        </>
      )}
    </div>
  );
}

/** Avanti e indietro, col totale: si sa sempre quanto c'è dietro. */
export function Pager({ total, limit, offset, onOffset }: { total: number; limit: number; offset: number; onOffset: (n: number) => void }) {
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);
  return (
    <div className="flex items-center gap-3 text-sm text-muted">
      <Button size="sm" variant="ghost" disabled={offset === 0} onClick={() => onOffset(Math.max(0, offset - limit))}>
        Precedenti
      </Button>
      <span>
        {from}–{to} di {total}
      </span>
      <Button size="sm" variant="ghost" disabled={to >= total} onClick={() => onOffset(offset + limit)}>
        Successivi
      </Button>
    </div>
  );
}
